import { ArrowBackOutlined, CheckCircleOutlined, OpenInNew, OpenInNewOutlined, PlaylistAddCheckOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { applyAllMemberHealthCheckFixes, getMemberHealthCheckIssues, MemberHealthCheckIssueResponse } from 'src/shared/services/healthCheckService';
import { FixConfirmationDialog } from '../components/fixConfirmationDialog';

export const StatisticsTroubleshootingCategoryPage = () => {
  const { t } = useTranslation(['troubleshooting', 'common']);
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();
  const [searchParams, setSearchParams] = useSearchParams();

  const associationId = searchParams.get('associationId') ? Number(searchParams.get('associationId')) : undefined;
  const districtId = searchParams.get('districtId') ? Number(searchParams.get('districtId')) : undefined;

  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [issues, setIssues] = useState<MemberHealthCheckIssueResponse[]>();
  const [reviewIssue, setReviewIssue] = useState<MemberHealthCheckIssueResponse>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);

  // Förvälj scope om användaren är begränsad — samma mönster som dashboarden.
  useEffect(() => {
    if (!currentUser || association || district || associationId || districtId) return;
    if (currentUser.districts.length === 0 && currentUser.associations.length === 1) {
      setAssociation(currentUser.associations[0] as unknown as Association);
    } else if (currentUser.associations.length === 0 && currentUser.districts.length === 1) {
      setDistrict(currentUser.districts[0] as unknown as District);
    }
  }, [currentUser, association, district, associationId, districtId]);

  // Synka URL när scopet ändras.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (association?.localAss_id) next.set('associationId', String(association.localAss_id));
    else next.delete('associationId');
    if (district?.countyAss_id) next.set('districtId', String(district.countyAss_id));
    else next.delete('districtId');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [association, district, searchParams, setSearchParams]);

  const fetchIssues = useCallback(() => {
    if (!category) return;
    setLoading(true);
    setHasError(false);
    getMemberHealthCheckIssues(category, associationId, districtId)
      .then((res) => {
        if (!isMounted()) return;
        setIssues(res.data);
      })
      .catch((e) => {
        ConsoleHelper.error(e);
        if (isMounted()) setHasError(true);
      })
      .finally(() => {
        if (isMounted()) setLoading(false);
      });
  }, [category, isMounted, associationId, districtId]);

  const navigateBack = () => {
    const params = new URLSearchParams();
    if (associationId) params.set('associationId', String(associationId));
    if (districtId) params.set('districtId', String(districtId));
    const query = params.toString();
    navigate(`${RoutesEnum.StatisticsTroubleshooting}${query ? `?${query}` : ''}`);
  };

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const handleFixApplied = (memberId: number) => {
    setIssues((prev) => prev?.filter((i) => i.memberId !== memberId));
    setReviewIssue(undefined);
  };

  const supportsAutoFix = issues?.some((i) => i.suggestedValue) ?? false;
  const hasGroupLabel = issues?.some((i) => i.groupLabel) ?? false;
  const hasPostNr = issues?.some((i) => i.postNr) ?? false;
  const fixableCount = issues?.filter((i) => i.suggestedValue).length ?? 0;

  const handleApplyAll = async () => {
    if (!category) return;
    setBulkApplying(true);
    try {
      const res = await applyAllMemberHealthCheckFixes(category, associationId, districtId);
      if (!isMounted()) return;
      const { applied, failed } = res.data;
      if (failed > 0) {
        toast.warning(t('troubleshooting:fix.bulkPartial', { applied, failed }) as string);
      } else {
        toast.success(t('troubleshooting:fix.bulkSuccess', { applied }) as string);
      }
      setBulkConfirmOpen(false);
      fetchIssues();
    } catch (e) {
      ConsoleHelper.error(e);
      if (isMounted()) toast.error(t('troubleshooting:fix.error') as string);
    } finally {
      if (isMounted()) setBulkApplying(false);
    }
  };

  // Slice issues för aktuell sida. Vid 20k+ rader skulle obegränsad rendering göra browsern segt.
  const pagedIssues = useMemo(() => {
    if (!issues) return [];
    const start = page * rowsPerPage;
    return issues.slice(start, start + rowsPerPage);
  }, [issues, page, rowsPerPage]);

  // Hoppa tillbaka till sida 1 om filtreringen ändras så vi inte fastnar på en tom sida.
  useEffect(() => {
    setPage(0);
  }, [category, associationId, districtId]);

  return (
    <GridWrapper>
      <Grid item xs={12}>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
          <IconButton size='small' onClick={navigateBack} aria-label={t('troubleshooting:backToSummary')}>
            <ArrowBackOutlined fontSize='small' />
          </IconButton>
          <MuiLink component='button' type='button' underline='hover' onClick={navigateBack} sx={{ fontSize: 13 }}>
            {t('troubleshooting:backToSummary')}
          </MuiLink>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'flex-end' }} justifyContent='space-between' spacing={2}>
          <MainContentHeader title={t(`troubleshooting:categories.${category}.header`, '')} />
          {currentUser && (
            <Box sx={{ display: 'flex', gap: 1.5, '& .MuiAutocomplete-inputRoot': { backgroundColor: 'white' } }}>
              <AssociationAndDistrictSelect
                hideDistrict={currentUser.districts.length === 0}
                association={association}
                district={district}
                associationCallback={(val) => setAssociation(val || undefined)}
                districtCallback={(val) => setDistrict(val || undefined)}
              />
            </Box>
          )}
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Alert
            severity='info'
            sx={{ mb: 3, '& .MuiAlert-action': { alignItems: 'center', pt: 0, pr: 1 } }}
            action={
              supportsAutoFix && fixableCount > 0 ? (
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<PlaylistAddCheckOutlined />}
                  onClick={() => setBulkConfirmOpen(true)}
                  disableElevation
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('troubleshooting:fix.applyAll', { count: fixableCount })}
                </Button>
              ) : undefined
            }
          >
            {t(`troubleshooting:categories.${category}.description`, '')}
          </Alert>
          {(() => {
            if (loading) {
              return (
                <Stack spacing={1}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant='rectangular' height={40} sx={{ borderRadius: 1 }} />
                  ))}
                </Stack>
              );
            }
            if (hasError) {
              return <Alert severity='error'>{t('troubleshooting:fetchError')}</Alert>;
            }
            if (!issues || issues.length === 0) {
              return (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CheckCircleOutlined sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant='h6' sx={{ mb: 1 }}>
                    {t('troubleshooting:noIssues')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('troubleshooting:noIssuesDescription')}
                  </Typography>
                </Box>
              );
            }
            return (
              <>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                      {hasGroupLabel && (
                        <TableCell>
                          {t(`troubleshooting:categories.${category}.groupColumn`, { defaultValue: t('troubleshooting:columns.group') })}
                        </TableCell>
                      )}
                      <TableCell>{t('troubleshooting:columns.memberNr')}</TableCell>
                      <TableCell>{t('troubleshooting:columns.name')}</TableCell>
                      {hasPostNr && <TableCell>{t('troubleshooting:columns.postNr')}</TableCell>}
                      <TableCell>{t('troubleshooting:columns.currentValue')}</TableCell>
                      {supportsAutoFix && <TableCell>{t('troubleshooting:columns.suggestedValue')}</TableCell>}
                      <TableCell align='right'>{t('troubleshooting:columns.action')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedIssues.map((issue, idx) => {
                      // Visa hushållets namn endast på första raden i varje grupp — repetition på följande
                      // rader gör tabellen rörig. Den underförstådda grupperingen syns ändå tydligt.
                      const prevGroup = idx > 0 ? pagedIssues[idx - 1].groupLabel : null;
                      const showGroupLabel = issue.groupLabel && issue.groupLabel !== prevGroup;
                      const isHead = issue.groupLabel && issue.description === 'Huvudmedlem — hushållets referensstatus.';
                      return (
                        <TableRow
                          key={`${issue.memberId}-${issue.groupLabel ?? ''}`}
                          hover
                          sx={{
                            // Subtil top-border när ett nytt hushåll börjar — visuell separator.
                            ...(showGroupLabel && idx > 0 ? { '& td': { borderTop: (theme) => `1px solid ${theme.palette.divider}` } } : {}),
                          }}
                        >
                          {hasGroupLabel && (
                            <TableCell sx={{ color: 'text.secondary', fontWeight: showGroupLabel ? 500 : 400 }}>
                              {showGroupLabel ? issue.groupLabel : ''}
                            </TableCell>
                          )}
                          <TableCell>
                            {issue.memberNr ? (
                              <MuiLink
                                component='button'
                                type='button'
                                underline='hover'
                                onClick={() => window.open(`${RoutesEnum.MemberDetails}/${issue.memberId}`, '_blank')}
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                              >
                                {issue.memberNr}
                                <OpenInNewOutlined sx={{ fontSize: 14 }} />
                              </MuiLink>
                            ) : (
                              '–'
                            )}
                          </TableCell>
                          <TableCell>
                            {`${issue.firstName ?? ''} ${issue.lastName ?? ''}`.trim()}
                            {isHead && (
                              <Typography component='span' variant='caption' sx={{ ml: 1, color: 'text.disabled', fontStyle: 'italic' }}>
                                ({t('troubleshooting:householdHead')})
                              </Typography>
                            )}
                          </TableCell>
                          {hasPostNr && <TableCell sx={{ color: 'text.secondary' }}>{issue.postNr ?? '–'}</TableCell>}
                          <TableCell sx={{ color: 'text.secondary' }}>{issue.currentValue ?? '–'}</TableCell>
                          {supportsAutoFix && <TableCell sx={{ color: 'primary.main', fontWeight: 500 }}>{issue.suggestedValue ?? '–'}</TableCell>}
                          <TableCell align='right'>
                            {issue.suggestedValue ? (
                              <Button size='small' variant='outlined' onClick={() => setReviewIssue(issue)}>
                                {t('troubleshooting:actions.review')}
                              </Button>
                            ) : (
                              <Button
                                size='small'
                                variant='outlined'
                                endIcon={<OpenInNew sx={{ fontSize: '0.85rem !important' }} />}
                                onClick={() => window.open(`${RoutesEnum.MemberDetails}/${issue.memberId}`, '_blank')}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {t('troubleshooting:actions.openMember')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  component='div'
                  count={issues.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100, 250]}
                  labelRowsPerPage={t('troubleshooting:pagination.rowsPerPage')}
                />
              </>
            );
          })()}
        </Paper>
      </Grid>
      {reviewIssue && category && (
        <FixConfirmationDialog
          open
          categoryKey={category}
          issue={reviewIssue}
          onClose={() => setReviewIssue(undefined)}
          onFixed={() => handleFixApplied(reviewIssue.memberId)}
        />
      )}
      <Dialog open={bulkConfirmOpen} onClose={() => !bulkApplying && setBulkConfirmOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{t('troubleshooting:fix.applyAllTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('troubleshooting:fix.applyAllConfirm', { count: fixableCount })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmOpen(false)} disabled={bulkApplying}>
            {t('common:button.cancel')}
          </Button>
          <Button
            onClick={handleApplyAll}
            variant='contained'
            disabled={bulkApplying}
            startIcon={bulkApplying ? <CircularProgress size={16} color='inherit' /> : undefined}
          >
            {bulkApplying ? t('troubleshooting:fix.applying') : t('troubleshooting:fix.applyAllConfirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </GridWrapper>
  );
};
